// Feature gating implemented - v2
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Spinner,
  Banner,
  Button,
  Checkbox,
  Avatar,
} from '@shopify/polaris';
import { useSite } from '../hooks/useSites';
import {
  useStandardReport,
  useEcommerceReport,
  useRealtimeReport,
  useTrafficSourcesReport,
  useHourlyAnalysisReport,
  usePagesAnalysisReport,
} from '../hooks/useReports';
import { useAuth } from '../hooks/useAuth';
import { useFeatures } from '../hooks/useFeatures';
import { LineChart, DoughnutChart, HeatMap } from '../components/charts';
import { Layout } from '../components/Layout';
import { DateRangePicker } from '../components/DateRangePicker';
import { downloadReport } from '../utils/downloadReport';

// Country code to flag URL mapping
const getCountryFlagUrl = (countryName: string): string => {
  const countryCodeMap: Record<string, string> = {
    'United States': 'US',
    'Bangladesh': 'BD',
    'Nigeria': 'NG',
    'China': 'CN',
    'Egypt': 'EG',
    'Canada': 'CA',
    'India': 'IN',
    'Vietnam': 'VN',
    'Morocco': 'MA',
    'United Kingdom': 'GB',
    'Germany': 'DE',
    'France': 'FR',
    'Japan': 'JP',
    'Australia': 'AU',
    'Brazil': 'BR',
    'Mexico': 'MX',
    'Spain': 'ES',
    'Italy': 'IT',
    'Netherlands': 'NL',
    'Russia': 'RU',
    'South Korea': 'KR',
    'Indonesia': 'ID',
    'Turkey': 'TR',
    'Saudi Arabia': 'SA',
    'Argentina': 'AR',
    'South Africa': 'ZA',
    'Pakistan': 'PK',
    'Philippines': 'PH',
    'Thailand': 'TH',
    'Malaysia': 'MY',
    'Poland': 'PL',
    'Belgium': 'BE',
    'Sweden': 'SE',
    'Switzerland': 'CH',
    'Austria': 'AT',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Finland': 'FI',
    'Ireland': 'IE',
    'Portugal': 'PT',
    'Greece': 'GR',
    'Czech Republic': 'CZ',
    'Romania': 'RO',
    'Hungary': 'HU',
    'Israel': 'IL',
    'Singapore': 'SG',
    'Hong Kong': 'HK',
    'Taiwan': 'TW',
    'New Zealand': 'NZ',
    'Chile': 'CL',
    'Colombia': 'CO',
    'Peru': 'PE',
    'Ukraine': 'UA',
  };

  const code = countryCodeMap[countryName];
  if (code) {
    return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`;
  }
  return '';
};

// Progress bar component
const ProgressBar = ({ percentage, color = '#5c6ac4' }: { percentage: number; color?: string }) => (
  <div style={{
    width: '100%',
    height: '8px',
    backgroundColor: '#e4e5e7',
    borderRadius: '4px',
    overflow: 'hidden'
  }}>
    <div style={{
      width: `${Math.min(percentage, 100)}%`,
      height: '100%',
      backgroundColor: color,
      borderRadius: '4px',
      transition: 'width 0.3s ease'
    }} />
  </div>
);

// Info icon component with custom tooltip (using portal to avoid overflow clipping)
const InfoIcon = ({ tooltip }: { tooltip?: string }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isHovered && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    }
  }, [isHovered]);

  return (
    <>
      <span
        ref={iconRef}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: '4px',
          cursor: 'help',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#2196F3',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
          }}
        >
          i
        </span>
      </span>
      {tooltip && isHovered && createPortal(
        <div
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            transform: 'translate(-50%, -100%)',
            padding: '8px 12px',
            backgroundColor: '#1a1a2e',
            color: '#ffffff',
            fontSize: '12px',
            borderRadius: '6px',
            whiteSpace: 'normal',
            width: '250px',
            textAlign: 'center',
            zIndex: 99999,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            lineHeight: '1.4',
            pointerEvents: 'none',
          }}
        >
          {tooltip}
          <span
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              borderWidth: '6px',
              borderStyle: 'solid',
              borderColor: '#1a1a2e transparent transparent transparent',
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
};

// Tooltip descriptions for metrics
const METRIC_TOOLTIPS = {
  sessions: 'Total number of sessions. A session is a period of time a user is actively engaged with your website.',
  pageViews: 'Total number of pages viewed. Repeated views of a single page are counted.',
  bounceRate: 'Percentage of single-page sessions with no interaction. Lower is better.',
  totalUsers: 'Total number of unique users who visited your site.',
  avgSessionDuration: 'Average length of a session. Longer duration indicates more engagement.',
  pagesPerSession: 'Average number of pages viewed per session. Higher means more engagement.',
  engagementRate: 'Percentage of engaged sessions. An engaged session lasts 10+ seconds or has 2+ page views.',
  topPages: 'Most visited pages on your website ranked by page views.',
  topReferrers: 'Websites that send the most traffic to your site.',
  topCountries: 'Countries where your visitors are located.',
  browsers: 'Web browsers used by your visitors.',
  operatingSystems: 'Operating systems used by your visitors.',
  userTypes: 'New visitors vs returning visitors to your site.',
  deviceTypes: 'Types of devices (desktop, mobile, tablet) used to access your site.',
};

// Metric Card with mini chart
const MetricCard = ({
  title,
  value,
  change,
  chartData,
  comparisonChartData,
  icon,
  tooltip
}: {
  title: string;
  value: string | number;
  change?: number;
  chartData?: Array<{ date: string; value: number }>;
  comparisonChartData?: Array<{ date: string; value: number }>;
  icon?: React.ReactNode;
  tooltip?: string;
}) => {
  const isPositive = change && change >= 0;

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            {icon && <span style={{ color: '#5c6ac4' }}>{icon}</span>}
            <Text as="span" variant="bodySm" fontWeight="semibold">{title}</Text>
            <InfoIcon tooltip={tooltip} />
          </InlineStack>
          {typeof change === 'number' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: isPositive ? '#008060' : '#d72c0d', fontSize: '14px' }}>
                {isPositive ? '↑' : '↓'}
              </span>
              <Text as="span" variant="bodySm" tone={isPositive ? 'success' : 'critical'}>
                {Math.abs(change).toFixed(0)}%
              </Text>
              <Text as="span" variant="bodySm" tone="subdued">
                vs. previous period
              </Text>
            </div>
          )}
        </InlineStack>

        <Text as="h2" variant="headingXl" fontWeight="bold">
          {value}
        </Text>

        {chartData && chartData.length > 0 && (
          <div style={{ height: '200px', marginTop: '8px', position: 'relative' }}>
            <LineChart
              data={chartData}
              comparisonData={comparisonChartData}
              label={title}
            />
          </div>
        )}
      </BlockStack>
    </Card>
  );
};

// Data Table Component
const DataTable = ({
  title,
  icon,
  columns,
  data,
  showFlags = false,
  tooltip
}: {
  title: string;
  icon?: React.ReactNode;
  columns: { key: string; label: string }[];
  data: Array<{ dimension: string; value: number; percentage?: number }>;
  showFlags?: boolean;
  tooltip?: string;
}) => (
  <div className="data-table-wrapper">
  <Card>
    <BlockStack gap="400">
      <InlineStack gap="200" blockAlign="center">
        {icon && <span style={{ color: '#5c6ac4' }}>{icon}</span>}
        <Text as="h2" variant="headingMd">{title}</Text>
        <InfoIcon tooltip={tooltip} />
      </InlineStack>

      {/* Table Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '28px minmax(0, 1fr) auto 70px',
        gap: '8px',
        padding: '8px 0',
        borderBottom: '1px solid #e4e5e7'
      }}>
        <div />
        <Text as="span" variant="bodySm" tone="subdued">{columns[0]?.label}</Text>
        <Text as="span" variant="bodySm" tone="subdued" alignment="end">{columns[1]?.label}</Text>
        <div />
      </div>

      {/* Table Body */}
      {data.map((item, index) => {
        const flagUrl = showFlags ? getCountryFlagUrl(item.dimension) : '';
        return (
          <div
            key={index}
            style={{
              display: 'grid',
              gridTemplateColumns: '28px minmax(0, 1fr) auto 70px',
              gap: '8px',
              alignItems: 'center',
              padding: '4px 0'
            }}
          >
            <Text as="span" variant="bodySm" tone="subdued">{index + 1}.</Text>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: 0,
              overflow: 'hidden'
            }}>
              {showFlags && flagUrl && (
                <img
                  src={flagUrl}
                  alt="country flag"
                  style={{ width: '20px', height: '14px', borderRadius: '2px', flexShrink: 0 }}
                />
              )}
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '13px'
              }}>
                {item.dimension}
              </span>
            </div>
            <Text as="span" variant="bodySm" alignment="end" fontWeight="medium">
              {item.value} ({item.percentage?.toFixed(1)}%)
            </Text>
            <ProgressBar percentage={item.percentage || 0} />
          </div>
        );
      })}
    </BlockStack>
  </Card>
  </div>
);

// Lock icon component for restricted features
const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

// Feature locked overlay component
const FeatureLockedOverlay = ({
  featureName,
  description,
  icon
}: {
  featureName: string;
  description?: string;
  icon?: React.ReactNode;
}) => {
  const navigate = useNavigate();

  return (
    <Card>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        textAlign: 'center',
        backgroundColor: '#f6f6f7',
        borderRadius: '8px',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: '#e4e5e7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px',
        }}>
          {icon || (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#8c9196" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          )}
        </div>
        <Text as="h3" variant="headingMd" fontWeight="bold">
          {featureName}
        </Text>
        <div style={{ marginTop: '8px', marginBottom: '20px', maxWidth: '400px' }}>
          <Text as="p" tone="subdued">
            {description || 'Upgrade your plan to unlock this feature and get deeper insights into your analytics.'}
          </Text>
        </div>
        <Button variant="primary" onClick={() => navigate('/pricing')}>
          Upgrade Plan
        </Button>
      </div>
    </Card>
  );
};

export function StandardReports() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    canAccessEcommerce,
    canAccessRealtime,
    canAccessTrafficSources,
    canAccessHourlyHeatmap,
    canAccessLandingExit,
    canAccessPdfExport,
    canAccessPeriodComparison,
    canAccessCustomDateRange,
  } = useFeatures();
  const [compareEnabled, setCompareEnabled] = useState(true);
  const reportRef = useRef<HTMLDivElement>(null);

  // Valid tab values
  type TabType = 'standard' | 'ecommerce' | 'realtime' | 'traffic-sources' | 'hourly' | 'pages';
  const validTabs: TabType[] = ['standard', 'ecommerce', 'realtime', 'traffic-sources', 'hourly', 'pages'];

  // Valid date range values
  type DateRangeType = 'last7days' | 'last15days' | 'last30days' | 'custom';
  const validDateRanges: DateRangeType[] = ['last7days', 'last15days', 'last30days', 'custom'];

  // Get initial tab from URL or default to 'standard'
  const getInitialTab = (): TabType => {
    const tabParam = searchParams.get('tab');
    if (tabParam && validTabs.includes(tabParam as TabType)) {
      return tabParam as TabType;
    }
    return 'standard';
  };

  // Get initial date range from URL or default to 'last30days'
  const getInitialDateRange = (): DateRangeType => {
    const rangeParam = searchParams.get('range');
    if (rangeParam && validDateRanges.includes(rangeParam as DateRangeType)) {
      return rangeParam as DateRangeType;
    }
    return 'last30days';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab);
  const [dateRange, setDateRange] = useState<DateRangeType>(getInitialDateRange);

  // Custom date range state - initialize from URL if custom range
  const [customStartDate, setCustomStartDate] = useState(() => searchParams.get('start') || '');
  const [customEndDate, setCustomEndDate] = useState(() => searchParams.get('end') || '');
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  // Update URL helper function
  const updateSearchParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams, { replace: true });
  };

  // Update URL when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    updateSearchParams({ tab: tab === 'standard' ? null : tab });
  };

  // Update URL when date range changes
  const handleDateRangeChange = (range: DateRangeType, start?: string, end?: string) => {
    setDateRange(range);
    if (range === 'custom' && start && end) {
      setCustomStartDate(start);
      setCustomEndDate(end);
      updateSearchParams({ range: 'custom', start, end });
    } else if (range === 'last30days') {
      // Remove range param for default
      updateSearchParams({ range: null, start: null, end: null });
    } else {
      updateSearchParams({ range, start: null, end: null });
    }
  };

  // PDF download loading state
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  const { data: site, isLoading: siteLoading } = useSite(siteId!);
  const { data: report, isLoading: reportLoading, error: reportError } = useStandardReport(
    siteId!,
    dateRange,
    dateRange === 'custom' ? customStartDate : undefined,
    dateRange === 'custom' ? customEndDate : undefined
  );

  // Check if site has e-commerce events enabled
  const hasEcommerceEvents = site?.analytics?.events && site.analytics.events.length > 0;

  // Fetch e-commerce report only when e-commerce tab is active and site has e-commerce events
  const { data: ecommerceReport, isLoading: ecommerceLoading, error: ecommerceError } = useEcommerceReport(
    siteId!,
    dateRange,
    dateRange === 'custom' ? customStartDate : undefined,
    dateRange === 'custom' ? customEndDate : undefined,
    activeTab === 'ecommerce' && hasEcommerceEvents
  );

  // Fetch realtime report - always available for GA4 properties
  const { data: realtimeReport, isLoading: realtimeLoading, error: realtimeError } = useRealtimeReport(siteId!);

  // Check if realtime is blocked due to plan limits
  const isRealtimeFeatureBlocked = realtimeError &&
    'code' in (realtimeError as any) &&
    (realtimeError as any).code === 'FEATURE_NOT_AVAILABLE';

  // Fetch traffic sources report
  const { data: trafficSourcesReport, isLoading: trafficSourcesLoading } = useTrafficSourcesReport(
    siteId!,
    dateRange,
    dateRange === 'custom' ? customStartDate : undefined,
    dateRange === 'custom' ? customEndDate : undefined
  );

  // Fetch hourly analysis report
  const { data: hourlyReport, isLoading: hourlyLoading, error: hourlyError } = useHourlyAnalysisReport(
    siteId!,
    dateRange,
    dateRange === 'custom' ? customStartDate : undefined,
    dateRange === 'custom' ? customEndDate : undefined
  );

  // Check if hourly heatmap is blocked due to plan limits
  const isHourlyFeatureBlocked = hourlyError &&
    'code' in (hourlyError as any) &&
    (hourlyError as any).code === 'FEATURE_NOT_AVAILABLE';

  // Fetch pages analysis report
  const { data: pagesReport, isLoading: pagesLoading } = usePagesAnalysisReport(
    siteId!,
    dateRange,
    dateRange === 'custom' ? customStartDate : undefined,
    dateRange === 'custom' ? customEndDate : undefined
  );

  const isLoading = siteLoading || reportLoading ||
    (activeTab === 'ecommerce' && ecommerceLoading) ||
    (activeTab === 'realtime' && realtimeLoading) ||
    (activeTab === 'traffic-sources' && trafficSourcesLoading) ||
    (activeTab === 'hourly' && hourlyLoading) ||
    (activeTab === 'pages' && pagesLoading);

  const calcChange = (current: number, previous: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Generate comparison chart data - if previous period has no data, create flat line at 0
  const getComparisonChartData = (
    currentData: Array<{ date: string; value: number }> | undefined,
    previousData: Array<{ date: string; value: number }> | undefined,
    multiplier: number = 1
  ): Array<{ date: string; value: number }> | undefined => {
    if (!compareEnabled || !currentData) return undefined;

    // If previous period has data, use it
    if (previousData && previousData.length > 0) {
      return multiplier === 1
        ? previousData
        : previousData.map(d => ({ ...d, value: Math.round(d.value * multiplier) }));
    }

    // If no previous period data, generate flat line at 0 using current period dates
    return currentData.map(d => ({ date: d.date, value: 0 }));
  };

  // Get date range label
  const getDateRangeLabel = () => {
    const today = new Date();
    let startDate = new Date();
    let endDate = today;

    const formatDate = (d: Date) => d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return `${formatDate(new Date(customStartDate))} - ${formatDate(new Date(customEndDate))}`;
    }

    switch (dateRange) {
      case 'last7days':
        startDate.setDate(today.getDate() - 6);
        break;
      case 'last15days':
        startDate.setDate(today.getDate() - 14);
        break;
      case 'last30days':
        startDate.setDate(today.getDate() - 29);
        break;
      default:
        startDate.setDate(today.getDate() - 29);
    }

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Format duration in seconds to readable format
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins < 60) {
      return `${mins}m ${secs}s`;
    }
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (isDownloadingPDF) return;

    setIsDownloadingPDF(true);
    try {
      await downloadReport(reportRef, 'standard');
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Spinner size="large" />
        </div>
      </Layout>
    );
  }

  if (reportError) {
    // Check if it's a Google Demo Account API access error
    const errorMessage = (reportError as Error)?.message || '';
    const isDemoAccountError = errorMessage.includes('Demo Account is not supported') ||
                               errorMessage.includes('denied access to the API') ||
                               errorMessage.includes('RESOURCE_EXHAUSTED') ||
                               errorMessage.includes('DEMO_ACCOUNT_NOT_SUPPORTED');

    return (
      <Layout>
        <BlockStack gap="400">
          <Text as="h1" variant="headingLg" fontWeight="bold">
            Standard Reports
          </Text>
          <Banner tone={isDemoAccountError ? "warning" : "critical"}>
            {isDemoAccountError ? (
              <>
                <Text as="p" fontWeight="semibold">Google Demo Account is not supported</Text>
                <Text as="p">
                  Google's Demo Account (like GA4 - Flood-It! or Google Merchandise Store) does not allow API access.
                  This is a restriction from Google, not an issue with Analyzely.
                  Please connect a real GA4 property to view analytics data.
                </Text>
              </>
            ) : (
              <BlockStack gap="300">
                <Text as="p">
                  Failed to load analytics data. Make sure GA4 is properly configured.
                </Text>
                <InlineStack gap="200">
                  <Button variant="primary" onClick={() => navigate(`/site/${siteId}/setup`)}>
                    Configure GA4
                  </Button>
                </InlineStack>
              </BlockStack>
            )}
          </Banner>
        </BlockStack>
      </Layout>
    );
  }

  return (
    <Layout>
      <div ref={reportRef}>
        <BlockStack gap="500">
          {/* Page Header */}
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h1" variant="headingLg" fontWeight="bold">
              Standard Reports
            </Text>
            <div className="report-download-wrapper">
              <Button
                icon={canAccessPdfExport ? DownloadIcon : LockIcon}
                onClick={canAccessPdfExport ? handleDownloadPDF : () => navigate('/pricing')}
                loading={isDownloadingPDF}
                tone={!canAccessPdfExport ? 'critical' : undefined}
              >
{canAccessPdfExport ? 'Download as PDF' : 'Download as PDF 🔒'}
              </Button>
            </div>
          </InlineStack>

          {/* User Info Card */}
          <Card>
            <InlineStack align="space-between" blockAlign="center">
              <InlineStack gap="400" blockAlign="center">
                <Avatar
                  name={site?.google_email || user?.email || 'User'}
                  initials={site?.google_email?.charAt(0) || user?.email?.charAt(0) || 'U'}
                />
                <BlockStack gap="100">
                  <Text as="h3" variant="headingMd">
                    {site?.google_email || user?.email}
                  </Text>
                  <Text as="span" variant="bodySm" tone="subdued">
                    {site?.analytics?.data_stream_name || 'Data Stream'} &lt; {site?.analytics?.property_id?.replace('properties/', '') || ''} &gt;
                  </Text>
                </BlockStack>
              </InlineStack>
              <Button variant="primary" tone="critical" onClick={() => navigate(`/site/${siteId}`)}>
                Logout
              </Button>
            </InlineStack>
          </Card>

          {/* Tab Selector - Always show with Realtime tab */}
          <Card>
            <InlineStack gap="200" wrap>
              <Button
                pressed={activeTab === 'standard'}
                onClick={() => handleTabChange('standard')}
                icon={<ChartIcon />}
              >
                Standard
              </Button>
              {hasEcommerceEvents && (
                <Button
                  pressed={activeTab === 'ecommerce'}
                  onClick={() => handleTabChange('ecommerce')}
                  icon={canAccessEcommerce ? <CartIcon /> : <LockIcon />}
                  tone={!canAccessEcommerce ? 'critical' : undefined}
                >
{canAccessEcommerce ? 'E-commerce' : 'E-commerce 🔒'}
                </Button>
              )}
              <Button
                pressed={activeTab === 'realtime'}
                onClick={() => handleTabChange('realtime')}
                icon={canAccessRealtime ? <RealtimeIcon /> : <LockIcon />}
                tone={!canAccessRealtime ? 'critical' : undefined}
              >
{canAccessRealtime ? 'Realtime' : 'Realtime 🔒'}
              </Button>
              <Button
                pressed={activeTab === 'traffic-sources'}
                onClick={() => handleTabChange('traffic-sources')}
                icon={canAccessTrafficSources ? <TrafficSourcesIcon /> : <LockIcon />}
                tone={!canAccessTrafficSources ? 'critical' : undefined}
              >
{canAccessTrafficSources ? 'Traffic Sources' : 'Traffic Sources 🔒'}
              </Button>
              <Button
                pressed={activeTab === 'hourly'}
                onClick={() => handleTabChange('hourly')}
                icon={canAccessHourlyHeatmap ? <HourlyIcon /> : <LockIcon />}
                tone={!canAccessHourlyHeatmap ? 'critical' : undefined}
              >
{canAccessHourlyHeatmap ? 'Hourly Analysis' : 'Hourly Analysis 🔒'}
              </Button>
              <Button
                pressed={activeTab === 'pages'}
                onClick={() => handleTabChange('pages')}
                icon={canAccessLandingExit ? <PagesIcon /> : <LockIcon />}
                tone={!canAccessLandingExit ? 'critical' : undefined}
              >
{canAccessLandingExit ? 'Landing & Exit' : 'Landing & Exit 🔒'}
              </Button>
            </InlineStack>
          </Card>

          {/* Date Range Selector - Hide for Realtime tab */}
          {activeTab !== 'realtime' && (
            <Card>
              <InlineStack align="space-between" blockAlign="center" wrap={false}>
                <InlineStack gap="200">
                  <Button
                    pressed={dateRange === 'last7days' && !showCustomPicker}
                    onClick={() => { handleDateRangeChange('last7days'); setShowCustomPicker(false); }}
                  >
                    Last 7 days
                  </Button>
                  <Button
                    pressed={dateRange === 'last15days' && !showCustomPicker}
                    onClick={() => { handleDateRangeChange('last15days'); setShowCustomPicker(false); }}
                  >
                    Last 15 days
                  </Button>
                  <Button
                    pressed={dateRange === 'last30days' && !showCustomPicker}
                    onClick={() => { handleDateRangeChange('last30days'); setShowCustomPicker(false); }}
                  >
                    Last 30 days
                  </Button>
                  {canAccessCustomDateRange && (
                    <Button
                      pressed={dateRange === 'custom' || showCustomPicker}
                      onClick={() => setShowCustomPicker(!showCustomPicker)}
                    >
                      Custom
                    </Button>
                  )}
                </InlineStack>

                <BlockStack gap="100">
                  <Text as="span" variant="bodySm" fontWeight="semibold" alignment="end">
                    {getDateRangeLabel()}
                  </Text>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
                    <Checkbox
                      label={`Compare to previous period${!canAccessPeriodComparison ? ' 🔒' : ''}`}
                      checked={canAccessPeriodComparison ? compareEnabled : false}
                      onChange={(checked) => canAccessPeriodComparison ? setCompareEnabled(checked) : navigate('/pricing')}
                      disabled={!canAccessPeriodComparison}
                    />
                  </div>
                </BlockStack>
              </InlineStack>

              {/* Custom Date Range Picker */}
              {showCustomPicker && (
                <DateRangePicker
                  startDate={customStartDate}
                  endDate={customEndDate}
                  onApply={(start, end) => {
                    handleDateRangeChange('custom', start, end);
                    setShowCustomPicker(false);
                  }}
                  onCancel={() => {
                    setShowCustomPicker(false);
                  }}
                />
              )}
            </Card>
          )}

          {/* Standard Reports Tab Content */}
          {activeTab === 'standard' && report && (
            <>
              {/* Metric Cards Row 1 - With Charts */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '16px'
              }}>
                <MetricCard
                  title="Sessions"
                  value={report.sessions.toLocaleString()}
                  change={compareEnabled ? calcChange(report.sessions, report.previousPeriod?.sessions || 0) : undefined}
                  chartData={report.sessionsOverTime}
                  comparisonChartData={getComparisonChartData(report.sessionsOverTime, report.previousPeriod?.sessionsOverTime)}
                  icon={<SessionsIcon />}
                  tooltip={METRIC_TOOLTIPS.sessions}
                />
                <MetricCard
                  title="Page Views"
                  value={report.pageViews.toLocaleString()}
                  change={compareEnabled ? calcChange(report.pageViews, report.previousPeriod?.pageViews || 0) : undefined}
                  chartData={report.sessionsOverTime?.map(d => ({ ...d, value: Math.round(d.value * 1.8) }))}
                  comparisonChartData={getComparisonChartData(report.sessionsOverTime, report.previousPeriod?.sessionsOverTime, 1.8)}
                  icon={<PageViewsIcon />}
                  tooltip={METRIC_TOOLTIPS.pageViews}
                />
                <MetricCard
                  title="Bounce Rate"
                  value={`${(report.bounceRate * 100).toFixed(1)}%`}
                  change={compareEnabled ? calcChange(report.bounceRate, report.previousPeriod?.bounceRate || 0) : undefined}
                  chartData={report.sessionsOverTime?.map(d => ({ ...d, value: report.bounceRate * 100 }))}
                  comparisonChartData={compareEnabled && report.sessionsOverTime ? report.sessionsOverTime.map(d => ({ ...d, value: (report.previousPeriod?.bounceRate || 0) * 100 })) : undefined}
                  icon={<BounceRateIcon />}
                  tooltip={METRIC_TOOLTIPS.bounceRate}
                />
              </div>

              {/* Metric Cards Row 2 - Additional Metrics */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px'
              }}>
                <MetricCard
                  title="Total Users"
                  value={report.users.toLocaleString()}
                  change={compareEnabled ? calcChange(report.users, report.previousPeriod?.users || 0) : undefined}
                  icon={<UsersIcon />}
                  tooltip={METRIC_TOOLTIPS.totalUsers}
                />
                <MetricCard
                  title="Avg. Session Duration"
                  value={formatDuration(report.avgSessionDuration)}
                  change={compareEnabled ? calcChange(report.avgSessionDuration, report.previousPeriod?.avgSessionDuration || 0) : undefined}
                  icon={<DurationIcon />}
                  tooltip={METRIC_TOOLTIPS.avgSessionDuration}
                />
                <MetricCard
                  title="Pages / Session"
                  value={report.sessions > 0 ? (report.pageViews / report.sessions).toFixed(2) : '0'}
                  change={compareEnabled ? calcChange(
                    report.sessions > 0 ? report.pageViews / report.sessions : 0,
                    (report.previousPeriod?.sessions || 0) > 0 ? (report.previousPeriod?.pageViews || 0) / (report.previousPeriod?.sessions || 1) : 0
                  ) : undefined}
                  icon={<PagesPerSessionIcon />}
                  tooltip={METRIC_TOOLTIPS.pagesPerSession}
                />
                <MetricCard
                  title="Engagement Rate"
                  value={report.sessions > 0 ? `${((report.engagedSessions / report.sessions) * 100).toFixed(1)}%` : '0%'}
                  change={compareEnabled ? calcChange(
                    report.sessions > 0 ? (report.engagedSessions / report.sessions) * 100 : 0,
                    (report.previousPeriod?.sessions || 0) > 0 ? ((report.previousPeriod?.engagedSessions || 0) / (report.previousPeriod?.sessions || 1)) * 100 : 0
                  ) : undefined}
                  icon={<EngagementIcon />}
                  tooltip={METRIC_TOOLTIPS.engagementRate}
                />
              </div>

              {/* Data Tables Row */}
              <div className="data-tables-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
                gap: '16px'
              }}>
                <DataTable
                  title="Top Pages"
                  icon={<PagesIcon />}
                  columns={[
                    { key: 'dimension', label: 'Page Path' },
                    { key: 'value', label: 'Page View' }
                  ]}
                  data={report.topPages}
                  tooltip={METRIC_TOOLTIPS.topPages}
                />
                <DataTable
                  title="Top Referrers"
                  icon={<ReferrersIcon />}
                  columns={[
                    { key: 'dimension', label: 'Referrer' },
                    { key: 'value', label: 'Session' }
                  ]}
                  data={report.topReferrers}
                  tooltip={METRIC_TOOLTIPS.topReferrers}
                />
                <DataTable
                    title="Top Countries"
                    icon={<CountriesIcon />}
                    columns={[
                      { key: 'dimension', label: 'Country' },
                      { key: 'value', label: 'Session' }
                    ]}
                    data={report.topCountries}
                    showFlags={true}
                    tooltip={METRIC_TOOLTIPS.topCountries}
                  />
              </div>

              {/* Browsers & Operating Systems Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '16px'
              }}>
                <DataTable
                      title="Browsers"
                      icon={<BrowsersIcon />}
                      columns={[
                        { key: 'dimension', label: 'Browser' },
                        { key: 'value', label: 'Sessions' }
                      ]}
                      data={report.browsers}
                      tooltip={METRIC_TOOLTIPS.browsers}
                    />
                    <DataTable
                      title="Operating Systems"
                      icon={<OSIcon />}
                      columns={[
                        { key: 'dimension', label: 'OS' },
                        { key: 'value', label: 'Sessions' }
                      ]}
                      data={report.operatingSystems}
                      tooltip={METRIC_TOOLTIPS.operatingSystems}
                    />
              </div>

              {/* Pie Charts Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '16px'
              }}>
                <Card>
                  <BlockStack gap="400">
                    <InlineStack gap="200" blockAlign="center">
                      <UserTypesIcon />
                      <Text as="h2" variant="headingMd">User Types</Text>
                      <InfoIcon tooltip={METRIC_TOOLTIPS.userTypes} />
                    </InlineStack>
                    <div style={{ height: '250px' }}>
                      <DoughnutChart
                        data={[
                          { dimension: 'New', value: report.newUsers, percentage: report.users > 0 ? (report.newUsers / report.users) * 100 : 0 },
                          { dimension: 'Returning', value: report.users - report.newUsers, percentage: report.users > 0 ? ((report.users - report.newUsers) / report.users) * 100 : 0 }
                        ]}
                      />
                    </div>
                  </BlockStack>
                </Card>
                <Card>
                    <BlockStack gap="400">
                      <InlineStack gap="200" blockAlign="center">
                        <DeviceTypesIcon />
                        <Text as="h2" variant="headingMd">Device Types</Text>
                        <InfoIcon tooltip={METRIC_TOOLTIPS.deviceTypes} />
                      </InlineStack>
                      <div style={{ height: '250px' }}>
                        <DoughnutChart data={report.deviceCategories} />
                      </div>
                    </BlockStack>
                  </Card>
              </div>
            </>
          )}

          {/* E-commerce Tab Content */}
          {activeTab === 'ecommerce' && hasEcommerceEvents && (
            <>
              {!canAccessEcommerce ? (
                <FeatureLockedOverlay
                  featureName="E-commerce Reports"
                  description="Track purchases, revenue, and conversion rates. Upgrade to Starter or higher to unlock e-commerce analytics."
                  icon={<CartIcon />}
                />
              ) : (
                <>
                  {ecommerceLoading && (
                    <Card>
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <Spinner size="large" />
                      </div>
                    </Card>
                  )}

                  {ecommerceError && (
                    <Banner tone="critical">
                      Failed to load e-commerce data. Make sure your GA4 property has e-commerce tracking enabled.
                    </Banner>
                  )}

                  {ecommerceReport && (
                <>
                  {/* E-commerce KPI Cards */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '16px'
                  }}>
                    <MetricCard
                      title="Total Revenue"
                      value={formatCurrency(ecommerceReport.totalRevenue, ecommerceReport.currency)}
                      change={compareEnabled ? calcChange(ecommerceReport.totalRevenue, ecommerceReport.previousPeriod?.totalRevenue || 0) : undefined}
                      icon={<RevenueIcon />}
                      tooltip="Total revenue from all completed purchases"
                    />
                    <MetricCard
                      title="Transactions"
                      value={ecommerceReport.transactions.toLocaleString()}
                      change={compareEnabled ? calcChange(ecommerceReport.transactions, ecommerceReport.previousPeriod?.transactions || 0) : undefined}
                      icon={<TransactionsIcon />}
                      tooltip="Total number of completed purchases"
                    />
                    <MetricCard
                      title="Avg. Order Value"
                      value={formatCurrency(ecommerceReport.averageOrderValue, ecommerceReport.currency)}
                      change={compareEnabled ? calcChange(ecommerceReport.averageOrderValue, ecommerceReport.previousPeriod?.averageOrderValue || 0) : undefined}
                      icon={<AOVIcon />}
                      tooltip="Average revenue per transaction (Total Revenue / Transactions)"
                    />
                    <MetricCard
                      title="Conversion Rate"
                      value={`${ecommerceReport.conversionRate.toFixed(2)}%`}
                      change={compareEnabled ? calcChange(
                        ecommerceReport.conversionRate,
                        (ecommerceReport.previousPeriod?.sessions || 0) > 0
                          ? ((ecommerceReport.previousPeriod?.transactions || 0) / (ecommerceReport.previousPeriod?.sessions || 1)) * 100
                          : 0
                      ) : undefined}
                      icon={<ConversionIcon />}
                      tooltip="Percentage of sessions that resulted in a purchase"
                    />
                  </div>

                  {/* Additional E-commerce Metrics */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '16px'
                  }}>
                    <MetricCard
                      title="Add to Carts"
                      value={ecommerceReport.addToCarts.toLocaleString()}
                      change={compareEnabled ? calcChange(ecommerceReport.addToCarts, ecommerceReport.previousPeriod?.addToCarts || 0) : undefined}
                      icon={<CartIcon />}
                      tooltip="Number of times items were added to cart"
                    />
                    <MetricCard
                      title="Checkouts Started"
                      value={ecommerceReport.checkouts.toLocaleString()}
                      change={compareEnabled ? calcChange(ecommerceReport.checkouts, ecommerceReport.previousPeriod?.checkouts || 0) : undefined}
                      icon={<CheckoutIcon />}
                      tooltip="Number of checkout sessions started"
                    />
                    <MetricCard
                      title="Product Views"
                      value={ecommerceReport.itemViews.toLocaleString()}
                      change={compareEnabled ? calcChange(ecommerceReport.itemViews, ecommerceReport.previousPeriod?.itemViews || 0) : undefined}
                      icon={<ProductViewIcon />}
                      tooltip="Number of product page views"
                    />
                    <MetricCard
                      title="Cart Abandonment"
                      value={`${ecommerceReport.cartAbandonmentRate.toFixed(1)}%`}
                      change={compareEnabled ? calcChange(
                        ecommerceReport.cartAbandonmentRate,
                        (ecommerceReport.previousPeriod?.addToCarts || 0) > 0
                          ? (((ecommerceReport.previousPeriod?.addToCarts || 0) - (ecommerceReport.previousPeriod?.transactions || 0)) / (ecommerceReport.previousPeriod?.addToCarts || 1)) * 100
                          : 0
                      ) : undefined}
                      icon={<AbandonmentIcon />}
                      tooltip="Percentage of carts that were abandoned without purchase"
                    />
                  </div>

                  {/* Revenue Over Time Chart */}
                  <Card>
                    <BlockStack gap="400">
                      <InlineStack gap="200" blockAlign="center">
                        <RevenueIcon />
                        <Text as="h2" variant="headingMd">Revenue Over Time</Text>
                      </InlineStack>
                      <div style={{ height: '300px' }}>
                        <LineChart
                          data={ecommerceReport.revenueOverTime}
                          label="Revenue"
                        />
                      </div>
                    </BlockStack>
                  </Card>

                  {/* Top Products and Conversion Funnel */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '16px'
                  }}>
                    {/* Top Products Table */}
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack gap="200" blockAlign="center">
                          <ProductViewIcon />
                          <Text as="h2" variant="headingMd">Top Products</Text>
                        </InlineStack>

                        {/* Table Header */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '40px 1fr 100px 80px 100px',
                          gap: '12px',
                          padding: '8px 0',
                          borderBottom: '1px solid #e4e5e7'
                        }}>
                          <div />
                          <Text as="span" variant="bodySm" tone="subdued">Product</Text>
                          <Text as="span" variant="bodySm" tone="subdued" alignment="end">Revenue</Text>
                          <Text as="span" variant="bodySm" tone="subdued" alignment="end">Qty</Text>
                          <div />
                        </div>

                        {/* Table Body */}
                        {ecommerceReport.topProducts.map((product, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '40px 1fr 100px 80px 100px',
                              gap: '12px',
                              alignItems: 'center',
                              padding: '4px 0'
                            }}
                          >
                            <Text as="span" variant="bodySm" tone="subdued">{index + 1}.</Text>
                            <Text as="span" variant="bodySm">{product.name}</Text>
                            <Text as="span" variant="bodySm" alignment="end">
                              {formatCurrency(product.revenue, ecommerceReport.currency)}
                            </Text>
                            <Text as="span" variant="bodySm" alignment="end">{product.quantity}</Text>
                            <ProgressBar percentage={product.percentage} color="#008060" />
                          </div>
                        ))}

                        {ecommerceReport.topProducts.length === 0 && (
                          <Text as="p" tone="subdued">No product data available</Text>
                        )}
                      </BlockStack>
                    </Card>

                    {/* Conversion Funnel */}
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack gap="200" blockAlign="center">
                          <ConversionIcon />
                          <Text as="h2" variant="headingMd">Conversion Funnel</Text>
                        </InlineStack>

                        <div style={{ padding: '16px 0' }}>
                          {/* Product Views */}
                          <FunnelStep
                            label="Product Views"
                            value={ecommerceReport.itemViews}
                            percentage={100}
                            color="#5c6ac4"
                          />
                          {/* Add to Cart */}
                          <FunnelStep
                            label="Add to Cart"
                            value={ecommerceReport.addToCarts}
                            percentage={ecommerceReport.itemViews > 0 ? (ecommerceReport.addToCarts / ecommerceReport.itemViews) * 100 : 0}
                            color="#50B83C"
                          />
                          {/* Checkout */}
                          <FunnelStep
                            label="Checkout"
                            value={ecommerceReport.checkouts}
                            percentage={ecommerceReport.itemViews > 0 ? (ecommerceReport.checkouts / ecommerceReport.itemViews) * 100 : 0}
                            color="#F5A623"
                          />
                          {/* Purchase */}
                          <FunnelStep
                            label="Purchase"
                            value={ecommerceReport.transactions}
                            percentage={ecommerceReport.itemViews > 0 ? (ecommerceReport.transactions / ecommerceReport.itemViews) * 100 : 0}
                            color="#008060"
                          />
                        </div>
                      </BlockStack>
                    </Card>
                  </div>
                </>
              )}
                </>
              )}
            </>
          )}

          {/* Realtime Tab Content */}
          {activeTab === 'realtime' && (
            <>
              {!canAccessRealtime ? (
                <FeatureLockedOverlay
                  featureName="Realtime Analytics"
                  description="See who's on your site right now with live visitor counts and active pages. Upgrade to Pro or higher to unlock realtime analytics."
                  icon={<RealtimeIcon />}
                />
              ) : (
                <>
                  {realtimeLoading && (
                    <Card>
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <Spinner size="large" />
                      </div>
                    </Card>
                  )}

                  {realtimeReport && (
                <>
                  {/* Live Status Banner */}
                  <Card>
                    <InlineStack align="space-between" blockAlign="center">
                      <InlineStack gap="300" blockAlign="center">
                        <span style={{
                          display: 'inline-block',
                          width: '12px',
                          height: '12px',
                          backgroundColor: '#22c55e',
                          borderRadius: '50%',
                          boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.3)'
                        }} />
                        <Text as="span" variant="headingMd" fontWeight="bold" tone="success">LIVE</Text>
                        <Text as="span" variant="bodyMd">
                          Active Users: <strong>{realtimeReport.activeUsers}</strong>
                        </Text>
                      </InlineStack>
                      <Text as="span" variant="bodySm" tone="subdued">
                        Last updated: {new Date(realtimeReport.timestamp).toLocaleTimeString()}
                      </Text>
                    </InlineStack>
                  </Card>

                  {/* Realtime Metric Cards */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '16px'
                  }}>
                    <MetricCard
                      title="Active Users"
                      value={realtimeReport.activeUsers.toLocaleString()}
                      icon={<UsersIcon />}
                      tooltip="Users currently active on your website"
                    />
                    <MetricCard
                      title="Page Views"
                      value={realtimeReport.pageViews.toLocaleString()}
                      icon={<PageViewsIcon />}
                      tooltip="Pages being viewed right now"
                    />
                    <MetricCard
                      title="Events"
                      value={realtimeReport.events.toLocaleString()}
                      icon={<EventsIcon />}
                      tooltip="Events triggered in real-time"
                    />
                  </div>

                  {/* Top Pages & Countries */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '16px'
                  }}>
                    <DataTable
                      title="Top Pages (Live)"
                      icon={<PagesIcon />}
                      columns={[
                        { key: 'dimension', label: 'Page' },
                        { key: 'value', label: 'Active Users' }
                      ]}
                      data={realtimeReport.topPages}
                      tooltip="Pages being viewed by active users right now"
                    />
                    <DataTable
                      title="Top Countries (Live)"
                      icon={<CountriesIcon />}
                      columns={[
                        { key: 'dimension', label: 'Country' },
                        { key: 'value', label: 'Active Users' }
                      ]}
                      data={realtimeReport.topCountries}
                      showFlags={true}
                      tooltip="Countries with active users right now"
                    />
                  </div>

                  {/* Device Categories */}
                  <Card>
                    <BlockStack gap="400">
                      <InlineStack gap="200" blockAlign="center">
                        <DeviceTypesIcon />
                        <Text as="h2" variant="headingMd">Device Types (Live)</Text>
                        <InfoIcon tooltip="Device types used by active users right now" />
                      </InlineStack>
                      <div style={{ height: '250px' }}>
                        <DoughnutChart data={realtimeReport.deviceCategories} />
                      </div>
                    </BlockStack>
                  </Card>

                  {/* Auto-refresh indicator */}
                  <Card>
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" variant="bodySm" tone="subdued">
                        Data refreshes automatically every 30 seconds
                      </Text>
                    </InlineStack>
                  </Card>
                </>
              )}

              {!realtimeLoading && !realtimeReport && (
                <>
                  {isRealtimeFeatureBlocked ? (
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack gap="300" blockAlign="center">
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: '#FFF4E5',
                          }}>
                            <RealtimeIcon />
                          </span>
                          <BlockStack gap="100">
                            <Text as="h2" variant="headingMd" fontWeight="bold">
                              Realtime Analytics
                            </Text>
                            <Text as="span" variant="bodySm" tone="subdued">
                              Available on Pro and Agency plans
                            </Text>
                          </BlockStack>
                        </InlineStack>

                        <Text as="p" variant="bodyMd">
                          Upgrade to Pro or Agency to see who's on your site right now, which pages they're viewing, and where they're coming from — all in real-time.
                        </Text>

                        <InlineStack gap="300">
                          <Button variant="primary" url="/pricing">
                            View Plans
                          </Button>
                          <Button url="/account">
                            Manage Subscription
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </Card>
                  ) : (
                    <Banner tone="warning">
                      Unable to fetch realtime data. Please ensure your GA4 property is properly configured.
                    </Banner>
                  )}
                </>
              )}
                </>
              )}
            </>
          )}

          {/* Traffic Sources Tab Content */}
          {activeTab === 'traffic-sources' && (
            <>
              {!canAccessTrafficSources ? (
                <FeatureLockedOverlay
                  featureName="Traffic Sources"
                  description="Understand where your visitors come from - search engines, social media, referrals, and more. Upgrade to Starter or higher to unlock traffic source analysis."
                  icon={<TrafficSourcesIcon />}
                />
              ) : (
                <>
                  {trafficSourcesLoading && (
                    <Card>
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <Spinner size="large" />
                      </div>
                    </Card>
                  )}

                  {trafficSourcesReport && (
                <>
                  {/* Summary Card */}
                  <Card>
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="span" variant="headingMd">Total Sessions from All Sources</Text>
                      <InlineStack gap="300" blockAlign="center">
                        {compareEnabled && trafficSourcesReport.previousPeriod && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                              color: calcChange(trafficSourcesReport.totalSessions, trafficSourcesReport.previousPeriod.totalSessions) >= 0 ? '#008060' : '#d72c0d',
                              fontSize: '14px'
                            }}>
                              {calcChange(trafficSourcesReport.totalSessions, trafficSourcesReport.previousPeriod.totalSessions) >= 0 ? '↑' : '↓'}
                            </span>
                            <Text as="span" variant="bodySm" tone={calcChange(trafficSourcesReport.totalSessions, trafficSourcesReport.previousPeriod.totalSessions) >= 0 ? 'success' : 'critical'}>
                              {Math.abs(calcChange(trafficSourcesReport.totalSessions, trafficSourcesReport.previousPeriod.totalSessions)).toFixed(0)}%
                            </Text>
                            <Text as="span" variant="bodySm" tone="subdued">vs. previous</Text>
                          </div>
                        )}
                        <Text as="span" variant="headingLg" fontWeight="bold">
                          {trafficSourcesReport.totalSessions.toLocaleString()}
                        </Text>
                      </InlineStack>
                    </InlineStack>
                  </Card>

                  {/* Traffic Sources and Mediums */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '16px'
                  }}>
                    {/* Top Sources Table */}
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack gap="200" blockAlign="center">
                          <TrafficSourcesIcon />
                          <Text as="h2" variant="headingMd">Top Sources</Text>
                          <InfoIcon tooltip="Websites and platforms that drive traffic to your site" />
                        </InlineStack>

                        {/* Table Header */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '40px 1fr 80px 80px 80px 100px',
                          gap: '8px',
                          padding: '8px 0',
                          borderBottom: '1px solid #e4e5e7'
                        }}>
                          <div />
                          <Text as="span" variant="bodySm" tone="subdued">Source</Text>
                          <Text as="span" variant="bodySm" tone="subdued" alignment="end">Sessions</Text>
                          <Text as="span" variant="bodySm" tone="subdued" alignment="end">Users</Text>
                          <Text as="span" variant="bodySm" tone="subdued" alignment="end">Bounce</Text>
                          <div />
                        </div>

                        {/* Table Body */}
                        {trafficSourcesReport.bySource.map((source, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '40px 1fr 80px 80px 80px 100px',
                              gap: '8px',
                              alignItems: 'center',
                              padding: '4px 0'
                            }}
                          >
                            <Text as="span" variant="bodySm" tone="subdued">{index + 1}.</Text>
                            <Text as="span" variant="bodySm">{source.source}</Text>
                            <Text as="span" variant="bodySm" alignment="end">{source.sessions.toLocaleString()}</Text>
                            <Text as="span" variant="bodySm" alignment="end">{source.users.toLocaleString()}</Text>
                            <Text as="span" variant="bodySm" alignment="end">{source.bounceRate.toFixed(1)}%</Text>
                            <ProgressBar percentage={source.percentage} color="#5c6ac4" />
                          </div>
                        ))}
                      </BlockStack>
                    </Card>

                    {/* Mediums Pie Chart */}
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack gap="200" blockAlign="center">
                          <MediumIcon />
                          <Text as="h2" variant="headingMd">Traffic by Medium</Text>
                          <InfoIcon tooltip="How users arrive at your site (organic, referral, cpc, etc.)" />
                        </InlineStack>
                        <div style={{ height: '300px' }}>
                          <DoughnutChart data={trafficSourcesReport.byMedium} />
                        </div>
                      </BlockStack>
                    </Card>
                  </div>

                  {/* UTM Campaigns Table */}
                  <Card>
                    <BlockStack gap="400">
                      <InlineStack gap="200" blockAlign="center">
                        <CampaignIcon />
                        <Text as="h2" variant="headingMd">UTM Campaigns</Text>
                        <InfoIcon tooltip="Performance of your marketing campaigns tracked via UTM parameters" />
                      </InlineStack>

                      {trafficSourcesReport.campaigns.length > 0 ? (
                        <>
                          {/* Table Header */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '40px 1fr 100px 100px 100px 120px',
                            gap: '12px',
                            padding: '8px 0',
                            borderBottom: '1px solid #e4e5e7'
                          }}>
                            <div />
                            <Text as="span" variant="bodySm" tone="subdued">Campaign</Text>
                            <Text as="span" variant="bodySm" tone="subdued" alignment="end">Sessions</Text>
                            <Text as="span" variant="bodySm" tone="subdued" alignment="end">Users</Text>
                            <Text as="span" variant="bodySm" tone="subdued" alignment="end">Bounce Rate</Text>
                            <div />
                          </div>

                          {/* Table Body */}
                          {trafficSourcesReport.campaigns.map((campaign, index) => (
                            <div
                              key={index}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '40px 1fr 100px 100px 100px 120px',
                                gap: '12px',
                                alignItems: 'center',
                                padding: '4px 0'
                              }}
                            >
                              <Text as="span" variant="bodySm" tone="subdued">{index + 1}.</Text>
                              <Text as="span" variant="bodySm">{campaign.campaign}</Text>
                              <Text as="span" variant="bodySm" alignment="end">{campaign.sessions.toLocaleString()}</Text>
                              <Text as="span" variant="bodySm" alignment="end">{campaign.users.toLocaleString()}</Text>
                              <Text as="span" variant="bodySm" alignment="end">{campaign.bounceRate.toFixed(1)}%</Text>
                              <ProgressBar percentage={campaign.percentage} color="#008060" />
                            </div>
                          ))}
                        </>
                      ) : (
                        <Text as="p" tone="subdued">
                          No UTM campaign data found. Use UTM parameters in your marketing links to track campaign performance.
                        </Text>
                      )}
                    </BlockStack>
                  </Card>
                </>
              )}

              {!trafficSourcesLoading && !trafficSourcesReport && (
                <Banner tone="warning">
                  Unable to fetch traffic sources data. Please ensure your GA4 property is properly configured.
                </Banner>
              )}
                </>
              )}
            </>
          )}

          {/* Hourly Analysis Tab Content */}
          {activeTab === 'hourly' && (
            <>
              {!canAccessHourlyHeatmap ? (
                <FeatureLockedOverlay
                  featureName="Hourly Heatmap"
                  description="Visualize when your visitors are most active with an interactive hourly heatmap. Upgrade to Pro or higher to unlock hourly analysis."
                  icon={<HourlyIcon />}
                />
              ) : (
                <>
                  {hourlyLoading && (
                    <Card>
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <Spinner size="large" />
                      </div>
                    </Card>
                  )}

                  {hourlyReport && (
                <>
                  {/* Summary Cards */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '16px'
                  }}>
                    <Card>
                      <BlockStack gap="200">
                        <Text as="span" variant="bodySm" tone="subdued">Total Sessions</Text>
                        <Text as="span" variant="headingLg" fontWeight="bold">
                          {hourlyReport.totalSessions.toLocaleString()}
                        </Text>
                      </BlockStack>
                    </Card>
                    <Card>
                      <BlockStack gap="200">
                        <Text as="span" variant="bodySm" tone="subdued">Peak Hour</Text>
                        <Text as="span" variant="headingLg" fontWeight="bold">
                          {DAY_NAMES[hourlyReport.peakHour.dayOfWeek]} {formatHour(hourlyReport.peakHour.hour)}
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                          {hourlyReport.peakHour.value.toLocaleString()} sessions
                        </Text>
                      </BlockStack>
                    </Card>
                    <Card>
                      <BlockStack gap="200">
                        <Text as="span" variant="bodySm" tone="subdued">Busiest Day</Text>
                        <Text as="span" variant="headingLg" fontWeight="bold">
                          {hourlyReport.busiestDay.dayName}
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                          {hourlyReport.busiestDay.totalValue.toLocaleString()} sessions
                        </Text>
                      </BlockStack>
                    </Card>
                  </div>

                  {/* Heatmap */}
                  <Card>
                    <BlockStack gap="400">
                      <InlineStack gap="200" blockAlign="center">
                        <HourlyIcon />
                        <Text as="h2" variant="headingMd">Traffic Heatmap by Day & Hour</Text>
                        <InfoIcon tooltip="Visualize when your website receives the most traffic. Darker cells indicate higher traffic." />
                      </InlineStack>
                      <div style={{ padding: '16px 0' }}>
                        <HeatMap data={hourlyReport.heatmapData} />
                      </div>
                    </BlockStack>
                  </Card>

                  {/* Tips */}
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">Insights</Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Use this data to schedule content updates, marketing campaigns, and social media posts during peak traffic hours for maximum visibility.
                      </Text>
                    </BlockStack>
                  </Card>
                </>
              )}

              {!hourlyLoading && !hourlyReport && (
                <>
                  {isHourlyFeatureBlocked ? (
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack gap="300" blockAlign="center">
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: '#FFF4E5',
                          }}>
                            <HourlyIcon />
                          </span>
                          <BlockStack gap="100">
                            <Text as="h2" variant="headingMd" fontWeight="bold">
                              Hourly Heatmap
                            </Text>
                            <Text as="span" variant="bodySm" tone="subdued">
                              Available on Pro and Agency plans
                            </Text>
                          </BlockStack>
                        </InlineStack>

                        <Text as="p" variant="bodyMd">
                          Upgrade to Pro or Agency to see when your visitors are most active. Discover peak traffic hours and days to optimize your content and marketing schedule.
                        </Text>

                        <InlineStack gap="300">
                          <Button variant="primary" url="/pricing">
                            View Plans
                          </Button>
                          <Button url="/account">
                            Manage Subscription
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </Card>
                  ) : (
                    <Banner tone="warning">
                      Unable to fetch hourly analysis data. Please ensure your GA4 property is properly configured.
                    </Banner>
                  )}
                </>
              )}
                </>
              )}
            </>
          )}

          {/* Landing & Exit Pages Tab Content */}
          {activeTab === 'pages' && (
            <>
              {!canAccessLandingExit ? (
                <FeatureLockedOverlay
                  featureName="Landing & Exit Pages"
                  description="See which pages visitors land on and where they leave your site. Upgrade to Starter or higher to unlock landing and exit page analysis."
                  icon={<PagesIcon />}
                />
              ) : (
                <>
                  {pagesLoading && (
                    <Card>
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <Spinner size="large" />
                      </div>
                    </Card>
                  )}

                  {pagesReport && (
                <>
                  {/* Summary Cards */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '16px'
                  }}>
                    <Card>
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="span" variant="bodySm" tone="subdued">Total Landing Sessions</Text>
                          {compareEnabled && pagesReport.previousPeriod && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{
                                color: calcChange(pagesReport.totalSessions, pagesReport.previousPeriod.totalSessions) >= 0 ? '#008060' : '#d72c0d',
                                fontSize: '12px'
                              }}>
                                {calcChange(pagesReport.totalSessions, pagesReport.previousPeriod.totalSessions) >= 0 ? '↑' : '↓'}
                              </span>
                              <Text as="span" variant="bodySm" tone={calcChange(pagesReport.totalSessions, pagesReport.previousPeriod.totalSessions) >= 0 ? 'success' : 'critical'}>
                                {Math.abs(calcChange(pagesReport.totalSessions, pagesReport.previousPeriod.totalSessions)).toFixed(0)}%
                              </Text>
                            </div>
                          )}
                        </InlineStack>
                        <Text as="span" variant="headingLg" fontWeight="bold">
                          {pagesReport.totalSessions.toLocaleString()}
                        </Text>
                      </BlockStack>
                    </Card>
                    <Card>
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="span" variant="bodySm" tone="subdued">Total Exits</Text>
                          {compareEnabled && pagesReport.previousPeriod && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{
                                color: calcChange(pagesReport.totalExits, pagesReport.previousPeriod.totalExits) >= 0 ? '#008060' : '#d72c0d',
                                fontSize: '12px'
                              }}>
                                {calcChange(pagesReport.totalExits, pagesReport.previousPeriod.totalExits) >= 0 ? '↑' : '↓'}
                              </span>
                              <Text as="span" variant="bodySm" tone={calcChange(pagesReport.totalExits, pagesReport.previousPeriod.totalExits) >= 0 ? 'success' : 'critical'}>
                                {Math.abs(calcChange(pagesReport.totalExits, pagesReport.previousPeriod.totalExits)).toFixed(0)}%
                              </Text>
                            </div>
                          )}
                        </InlineStack>
                        <Text as="span" variant="headingLg" fontWeight="bold">
                          {pagesReport.totalExits.toLocaleString()}
                        </Text>
                      </BlockStack>
                    </Card>
                  </div>

                  {/* Landing and Exit Pages Tables */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                    gap: '16px'
                  }}>
                    {/* Landing Pages Table */}
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack gap="200" blockAlign="center">
                          <LandingIcon />
                          <Text as="h2" variant="headingMd">Landing Pages</Text>
                          <InfoIcon tooltip="Pages where users first enter your website" />
                        </InlineStack>

                        {/* Table Header */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '30px 1fr 70px 70px 80px',
                          gap: '8px',
                          padding: '8px 0',
                          borderBottom: '1px solid #e4e5e7'
                        }}>
                          <div />
                          <Text as="span" variant="bodySm" tone="subdued">Page</Text>
                          <Text as="span" variant="bodySm" tone="subdued" alignment="end">Sessions</Text>
                          <Text as="span" variant="bodySm" tone="subdued" alignment="end">Bounce</Text>
                          <div />
                        </div>

                        {/* Table Body */}
                        {pagesReport.landingPages.slice(0, 10).map((page, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '30px 1fr 70px 70px 80px',
                              gap: '8px',
                              alignItems: 'center',
                              padding: '4px 0'
                            }}
                          >
                            <Text as="span" variant="bodySm" tone="subdued">{index + 1}.</Text>
                            <Text as="span" variant="bodySm" truncate>{page.page}</Text>
                            <Text as="span" variant="bodySm" alignment="end">{page.sessions.toLocaleString()}</Text>
                            <Text as="span" variant="bodySm" alignment="end">{page.bounceRate.toFixed(0)}%</Text>
                            <ProgressBar percentage={page.percentage} color="#5c6ac4" />
                          </div>
                        ))}
                      </BlockStack>
                    </Card>

                    {/* Exit Pages Table */}
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack gap="200" blockAlign="center">
                          <ExitIcon />
                          <Text as="h2" variant="headingMd">Exit Pages</Text>
                          <InfoIcon tooltip="Pages where users leave your website" />
                        </InlineStack>

                        {/* Table Header */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '30px 1fr 70px 70px 80px',
                          gap: '8px',
                          padding: '8px 0',
                          borderBottom: '1px solid #e4e5e7'
                        }}>
                          <div />
                          <Text as="span" variant="bodySm" tone="subdued">Page</Text>
                          <Text as="span" variant="bodySm" tone="subdued" alignment="end">Exits</Text>
                          <Text as="span" variant="bodySm" tone="subdued" alignment="end">Exit %</Text>
                          <div />
                        </div>

                        {/* Table Body */}
                        {pagesReport.exitPages.slice(0, 10).map((page, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '30px 1fr 70px 70px 80px',
                              gap: '8px',
                              alignItems: 'center',
                              padding: '4px 0'
                            }}
                          >
                            <Text as="span" variant="bodySm" tone="subdued">{index + 1}.</Text>
                            <Text as="span" variant="bodySm" truncate>{page.page}</Text>
                            <Text as="span" variant="bodySm" alignment="end">{page.exits.toLocaleString()}</Text>
                            <Text as="span" variant="bodySm" alignment="end">{page.exitRate.toFixed(0)}%</Text>
                            <ProgressBar percentage={page.percentage} color="#d72c0d" />
                          </div>
                        ))}
                      </BlockStack>
                    </Card>
                  </div>

                  {/* Tips */}
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">Insights</Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        High bounce rates on landing pages indicate users aren't finding what they expected. High exit rates on non-checkout pages may indicate issues with user experience or content.
                      </Text>
                    </BlockStack>
                  </Card>
                </>
              )}

              {!pagesLoading && !pagesReport && (
                <Banner tone="warning">
                  Unable to fetch pages analysis data. Please ensure your GA4 property is properly configured.
                </Banner>
              )}
                </>
              )}
            </>
          )}
        </BlockStack>
      </div>
    </Layout>
  );
}

// Day names for heatmap
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Format hour for display
const formatHour = (hour: number): string => {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

// Funnel Step Component
const FunnelStep = ({ label, value, percentage, color }: { label: string; value: number; percentage: number; color: string }) => (
  <div style={{ marginBottom: '16px' }}>
    <InlineStack align="space-between" blockAlign="center">
      <Text as="span" variant="bodySm" fontWeight="semibold">{label}</Text>
      <Text as="span" variant="bodySm">{value.toLocaleString()} ({percentage.toFixed(1)}%)</Text>
    </InlineStack>
    <div style={{
      marginTop: '8px',
      width: '100%',
      height: '24px',
      backgroundColor: '#e4e5e7',
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      <div style={{
        width: `${Math.min(percentage, 100)}%`,
        height: '100%',
        backgroundColor: color,
        borderRadius: '4px',
        transition: 'width 0.3s ease',
        minWidth: percentage > 0 ? '4px' : '0'
      }} />
    </div>
  </div>
);

// Currency formatter
const formatCurrency = (value: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

// Icons
const DownloadIcon = () => (
  <svg viewBox="0 0 20 20" width="20" height="20" fill="currentColor">
    <path d="M13 8V2H7v6H2l8 8 8-8h-5zM0 18h20v2H0v-2z"/>
  </svg>
);

const SessionsIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"/>
  </svg>
);

const PageViewsIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
  </svg>
);

const BounceRateIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd"/>
  </svg>
);

const PagesIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
  </svg>
);

const ReferrersIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd"/>
  </svg>
);

const CountriesIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd"/>
  </svg>
);

const UserTypesIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
  </svg>
);

const DeviceTypesIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zm3 14a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
  </svg>
);

const DurationIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
  </svg>
);

const PagesPerSessionIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
  </svg>
);

const EngagementIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/>
  </svg>
);

const BrowsersIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd"/>
  </svg>
);

const OSIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
  </svg>
);

// Tab Icons
const ChartIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"/>
    <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"/>
  </svg>
);

const CartIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
  </svg>
);

// E-commerce Icons
const RevenueIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
  </svg>
);

const TransactionsIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/>
  </svg>
);

const AOVIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd"/>
  </svg>
);

const ConversionIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"/>
  </svg>
);

const CheckoutIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clipRule="evenodd"/>
  </svg>
);

const ProductViewIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
  </svg>
);

const AbandonmentIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd"/>
  </svg>
);

// Realtime Icons
const RealtimeIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
  </svg>
);

const EventsIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
  </svg>
);

// Traffic Sources Icons
const TrafficSourcesIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
  </svg>
);

const MediumIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm9 4a1 1 0 10-2 0v6a1 1 0 102 0V7zm-3 2a1 1 0 10-2 0v4a1 1 0 102 0V9zm-3 3a1 1 0 10-2 0v1a1 1 0 102 0v-1z" clipRule="evenodd"/>
  </svg>
);

const CampaignIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd"/>
  </svg>
);

// Hourly Analysis Icons
const HourlyIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
  </svg>
);

// Landing & Exit Pages Icons
const LandingIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd"/>
  </svg>
);

const ExitIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd"/>
  </svg>
);
